import { useMemo, useState } from 'react';
import { useTabState } from '../lib/tabParam';
import { useGroupCount, groupTabLabel } from '../lib/groups';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { api, ApiClientError } from '../lib/api';
import { downloadDocument } from '../lib/downloadDocument';
import { Badge, Button } from '../atoms';
import {
  Tabs, DataTable, StatCard, InlineEditCard,
  EntityActivityFeed, EntityNotesPanel, EntityGroupsPanel, Modal, useToast,
} from '../organisms';
import { EntityHeader } from '../shell';
import { useEntityPermission } from '../lib/usePermissions';
import { latestAttr, humanizeToken, type AttrMap } from '../lib/inlineAttribution';
import { formatDate } from '../lib/orgTimezone';
import { LEVEL_LABEL, LEVEL_ORDER, type RecipientLevel } from '../lib/recipientLevel';

/**
 * InformationRequestDetail — /information-requests/:id
 *
 * The tab-view profile for one client information request, matching the standard
 * EntityHeader + Tabs layout the client / parcel / government profiles use (the
 * flat schema-driven GenericDetail is the regression this restores). Config-light,
 * reusing the shared organisms.
 *
 *   - Overview : the same schema-driven field grid GenericDetail renders
 *                (DetailFieldGrid over GET /api/information-requests/:id).
 *   - Items    : the request's requested_items (GET /api/information-requests/:id/items).
 *   - Activity : per-entity humanized audit feed (entityType 'information_requests').
 *   - Notes    : the notes thread (entityType 'information_requests').
 *
 * There is no Documents tab: the documents table carries no information_request_id
 * FK, so a Documents surface here would need a schema change (blocked-on-schema).
 *
 * Domain 03.1 — RBAC, Identity & Org Settings. FE-only + one minimal read
 * endpoint (the items list); no schema change.
 */

// The raw information_requests row from GET /api/information-requests/:id.
interface InformationRequest {
  id: string;
  client_id: string | null;
  property_id: string | null;
  title: string;
  type: string | null;
  status: 'draft' | 'sent' | 'received' | 'partial' | 'complete' | 'cancelled';
  tax_year: number | null;
  due_date: string | null;
  received_date: string | null;
  [key: string]: unknown;
}

// A requested_items row from GET /api/information-requests/:id/items.
interface RequestedItem {
  id: string;
  description: string;
  category: string | null;
  status: string | null;
  received_date: string | null;
  notes: string | null;
  sort_order: number | null;
  // Set when a portal upload fulfilled this item — lets staff open the actual file.
  document_id: string | null;
  document_name: string | null;
}

const statusVariant: Record<InformationRequest['status'], 'neutral' | 'info' | 'warning' | 'success'> = {
  draft: 'neutral', sent: 'info', received: 'success', partial: 'warning', complete: 'success', cancelled: 'neutral',
};
const statusLabel: Record<InformationRequest['status'], string> = {
  // 'received' (all items in) collapses to "Complete" to match the board's boardStatus()
  // — the same request must not read "Complete" in the list and "Received" on its profile.
  draft: 'Draft', sent: 'Sent', received: 'Complete', partial: 'Partial', complete: 'Complete', cancelled: 'Cancelled',
};

/** Today's date as YYYY-MM-DD for a bare date-string comparison (overdue). */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** A request is overdue when it has a past due date and is still open. */
function isOverdue(req: InformationRequest): boolean {
  if (!req.due_date) return false;
  if (['complete', 'received', 'cancelled'].includes(req.status)) return false;
  return req.due_date.slice(0, 10) < todayIso();
}

// A properties row from GET /api/properties?client_id= (the client's current list).
interface ClientProperty {
  id: string;
  name: string;
  location_code: string | null;
  city: string | null;
  state: string | null;
  status: string | null;
}

type Tab = 'overview' | 'items' | 'properties' | 'notes' | 'groups' | 'activity';

export function InformationRequestDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [tab, setTab] = useTabState<Tab>(['overview', 'items', 'properties', 'notes', 'groups', 'activity'] as const, 'overview');
  const groupCount = useGroupCount('information_request', id);
  const [cancelOpen, setCancelOpen] = useState(false);

  // Inline-edit affordance mirrors the server gate (SoD; default-deny while loading).
  const canEdit = useEntityPermission('information_request', 'can_edit');

  const recordQuery = useQuery<InformationRequest>({
    queryKey: ['information_requests', id],
    queryFn: ({ signal }) => api.get<InformationRequest>(`/api/information-requests/${id}`, signal),
    enabled: !!id,
  });

  // Field-level attribution ("last changed by · when") for the inline-edit cards.
  const attrQuery = useQuery<AttrMap>({
    queryKey: ['field-attribution', 'information_requests', id],
    queryFn: ({ signal }) => api.get<AttrMap>(`/api/field-attribution/information_requests/${id}`, signal),
    enabled: !!id,
  });

  const itemsQuery = useQuery<RequestedItem[]>({
    queryKey: ['requested-items', id],
    queryFn: ({ signal }) => api.get<RequestedItem[]>(`/api/information-requests/${id}/items`, signal),
    enabled: !!id,
  });

  // Cancel (withdraw) the request — removes it from the board + the client portal.
  const cancelMut = useMutation({
    mutationFn: () => api.post(`/api/information-requests/${id}/cancel`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['information_requests', id] });
      queryClient.invalidateQueries({ queryKey: ['information-requests-board'] });
      toast({ variant: 'success', message: 'Request cancelled.' });
      setCancelOpen(false);
      navigate('/information-requests');
    },
    onError: (err) => toast({
      variant: 'error',
      message: err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error) : 'Could not cancel the request.',
    }),
  });

  const record = recordQuery.data;

  // Client + property names for the header context. Independent, best-effort reads
  // (the Overview grid still carries the raw ids); a failure just drops the name.
  const clientQuery = useQuery<{ id: string; name: string | null }>({
    queryKey: ['clients', record?.client_id],
    queryFn: ({ signal }) => api.get<{ id: string; name: string | null }>(`/api/clients/${record!.client_id}`, signal),
    enabled: !!record?.client_id,
  });
  const propertyQuery = useQuery<{ id: string; name: string | null }>({
    queryKey: ['properties', record?.property_id],
    queryFn: ({ signal }) => api.get<{ id: string; name: string | null }>(`/api/properties/${record!.property_id}`, signal),
    enabled: !!record?.property_id,
  });

  // Property list — the client's CURRENT properties (GET /api/properties?client_id=),
  // surfaced so staff can confirm additions/deletions against the request. This is
  // the "attach current property list" step of the annual info-request campaign.
  const clientPropertiesQuery = useQuery<ClientProperty[]>({
    queryKey: ['client-properties', record?.client_id],
    queryFn: ({ signal }) => api.get<ClientProperty[]>(`/api/properties?client_id=${record!.client_id}`, signal),
    enabled: !!record?.client_id,
  });

  // 7/28 2.4 — the contact LEVEL(s) the request was delivered to (Client / Property /
  // Parcel), re-derived server-side from the stored sent_to emails. Only fetched once
  // the request has been sent (has a sent_to); a draft never queries.
  const recipientLevelsQuery = useQuery<{ levels: RecipientLevel[] }>({
    queryKey: ['information-request-recipient-levels', id],
    queryFn: ({ signal }) => api.get<{ levels: RecipientLevel[] }>(`/api/information-requests/${id}/recipient-levels`, signal),
    enabled: !!id && !!(record && record.sent_to),
  });

  // Notes count for the tab badge — shares EntityNotesPanel's query key so the
  // fetch is deduped and any add/edit/delete invalidation refreshes both.
  const notesQuery = useQuery<Array<Record<string, unknown>>>({
    queryKey: ['entity-notes', 'information_requests', id],
    queryFn: ({ signal }) => api.get<Array<Record<string, unknown>>>(`/api/notes-enriched/information_requests/${id}`, signal),
    enabled: !!id,
  });

  if (recordQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading information request…</p>;
  }

  const error = recordQuery.error;
  if (error) {
    const notFound = error instanceof ApiClientError && error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>
          {notFound ? 'Information request not found' : 'Failed to load information request'}
        </h2>
        <p style={{ margin: 0 }}>{(error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/information-requests')}>
            Back to Information requests
          </Button>
        </p>
      </div>
    );
  }

  if (!record || !id) return null;

  const items = itemsQuery.data ?? [];
  const notes = notesQuery.data ?? [];
  const clientProperties = clientPropertiesQuery.data ?? [];
  // The delivered contact level(s), ordered broadest→narrowest for a stable label.
  // Empty for a draft/legacy request (or when no delivered email is still derivable).
  const recipientLevels = LEVEL_ORDER.filter((l) => (recipientLevelsQuery.data?.levels ?? []).includes(l));
  const itemsTotal = items.length;
  const itemsReceived = items.filter((i) => !!i.received_date).length;
  const itemsOutstanding = Math.max(0, itemsTotal - itemsReceived);
  const overdue = isOverdue(record);

  const attr: AttrMap = attrQuery.data ?? {};
  const recordFields = record as unknown as Record<string, unknown>;
  const invalidate: unknown[][] = [
    ['information_requests', id],
    ['information-requests-board'],
    ['field-attribution', 'information_requests', id],
  ];
  const openProperty = (pid: string) => navigate(`/properties/${pid}`);

  const clientName = clientQuery.data?.name ?? null;
  const propertyName = propertyQuery.data?.name ?? null;
  const subtitleParts = [
    clientName,
    propertyName,
    record.tax_year != null ? `Tax year ${record.tax_year}` : null,
  ].filter(Boolean) as string[];

  return (
    <>
      <EntityHeader
        prefix="Information request"
        title={record.title}
        subtitle={subtitleParts.length > 0 ? subtitleParts.join(' · ') : undefined}
        statusPills={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <Badge variant={statusVariant[record.status] ?? 'neutral'} dot>
              {statusLabel[record.status] ?? String(record.status).replace(/_/g, ' ')}
            </Badge>
            {overdue && <Badge variant="warning" dot>Overdue</Badge>}
          </span>
        }
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => navigate('/information-requests')}>
              Back
            </Button>
            {canEdit && record.status !== 'cancelled' && (
              <Button variant="ghost" size="sm" onClick={() => setCancelOpen(true)} style={{ color: 'var(--color-status-red)' }}>
                Cancel request
              </Button>
            )}
          </>
        }
        kpis={
          <>
            <StatCard
              label="Items received"
              value={`${itemsReceived} / ${itemsTotal}`}
              caption={itemsTotal === 0 ? 'no items on file' : 'received of requested'}
              accent="hunter"
            />
            <StatCard
              label="Outstanding"
              value={String(itemsOutstanding)}
              caption={itemsOutstanding > 0 ? 'not yet received' : 'all items received'}
              accent={itemsOutstanding > 0 ? 'amber' : 'green'}
            />
            <StatCard
              label="Due"
              value={record.due_date ? formatDate(record.due_date, { dateOnly: true }) : '—'}
              caption={overdue ? 'past due date' : record.due_date ? 'on track' : 'no due date'}
              accent={overdue ? 'red' : 'info'}
            />
          </>
        }
      />

      <Tabs
        activeValue={tab}
        onChange={(v) => setTab(v as Tab)}
        items={[
          { value: 'overview', label: 'Overview' },
          { value: 'items', label: 'Items', count: itemsTotal },
          // Annual info-request campaign: the client's current property list.
          { value: 'properties', label: 'Property list', count: clientProperties.length },
          { value: 'notes', label: 'Notes', count: notes.length },
          { value: 'groups', label: groupTabLabel(groupCount) },
          { value: 'activity', label: 'Activity' },
        ]}
      />

      <div style={{ marginTop: 'var(--sp-5)' }}>
        {tab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', maxWidth: 560 }}>
            {/* Editable request fields (the 9th inline-edit profile). Notes live on the
                Notes tab, so the record's own note stays a compact field here — not a
                second big notes box. */}
            <InlineEditCard
              title="Request details"
              routePath="information-requests"
              recordId={id}
              record={recordFields}
              canEdit={canEdit}
              invalidateKeys={invalidate}
              attribution={latestAttr(attr, ['title', 'tax_year', 'due_date', 'notes'])}
              fields={[
                { key: 'title', label: 'Title', type: 'text', required: true },
                { key: 'type', label: 'Type', readOnly: true, display: (v) => (v ? humanizeToken(String(v)) : '—') },
                { key: 'tax_year', label: 'Tax year', type: 'number' },
                { key: 'due_date', label: 'Due date', type: 'date', display: (v) => (v ? formatDate(String(v), { dateOnly: true }) : '—') },
                { key: 'notes', label: 'Notes', type: 'textarea' },
              ]}
            />
            {/* Delivery + lifecycle are workflow-managed (Send / fulfillment), so this
                card is read-only — it shows no Edit affordance. */}
            <InlineEditCard
              title="Delivery & status"
              routePath="information-requests"
              recordId={id}
              record={recordFields}
              canEdit={false}
              invalidateKeys={invalidate}
              attribution={latestAttr(attr, ['status', 'sent_to', 'sent_date', 'received_date'])}
              fields={[
                { key: 'status', label: 'Status', readOnly: true, display: (v) => (v ? humanizeToken(String(v)) : '—') },
                { key: 'sent_to', label: 'Sent to', readOnly: true },
                { key: 'sent_date', label: 'Sent', readOnly: true, display: (v) => (v ? formatDate(String(v), { dateOnly: true }) : '—') },
                { key: 'received_date', label: 'Received', readOnly: true, display: (v) => (v ? formatDate(String(v), { dateOnly: true }) : '—') },
              ]}
            />
            {/* 7/28 2.4: the contact LEVEL(s) the request was delivered to, snug under the
                Delivery card (beside "Sent to"). Server-derived from the sent_to emails;
                only shown for a sent request, and hidden when no level is derivable. */}
            {recipientLevels.length > 0 && (
              <div style={{ marginTop: 'calc(-1 * var(--sp-2))', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
                Contact level:{' '}
                <span style={{ color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)' }}>
                  {recipientLevels.map((l) => LEVEL_LABEL[l]).join(' · ')}
                </span>
              </div>
            )}
            {/* 7/28 C5: the owning client, linked back to the client profile — at the BOTTOM,
                beneath the other sections, mirroring the property-profile client link. */}
            {record.client_id && (
              <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
                Client:{' '}
                <a onClick={() => navigate(`/clients/${record.client_id}`)} style={{ color: 'var(--brand-primary)', cursor: 'pointer', fontWeight: 'var(--fw-medium)' }}>{clientName ?? record.client_id} ↗</a>
              </div>
            )}
          </div>
        )}
        {tab === 'items' && (
          <ItemsTab
            items={items}
            loading={itemsQuery.isLoading}
            error={itemsQuery.error as Error | null}
            requestId={id}
            canEdit={canEdit}
          />
        )}
        {tab === 'properties' && (
          <PropertyListTab
            properties={clientProperties}
            loading={clientPropertiesQuery.isLoading}
            error={clientPropertiesQuery.error as Error | null}
            hasClient={!!record.client_id}
            onOpenProperty={openProperty}
          />
        )}
        {tab === 'notes' && <EntityNotesPanel entityType="information_requests" entityId={id} />}
        {tab === 'groups' && <EntityGroupsPanel entityType="information_request" entityId={id} />}
        {tab === 'activity' && <EntityActivityFeed entityType="information_requests" entityId={id} />}
      </div>

      <Modal
        open={cancelOpen}
        onClose={() => { if (!cancelMut.isPending) setCancelOpen(false); }}
        title="Cancel this information request?"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelOpen(false)} disabled={cancelMut.isPending}>Keep it</Button>
            <Button variant="danger" loading={cancelMut.isPending} onClick={() => cancelMut.mutate()}>Yes, cancel it</Button>
          </>
        }
      >
        <p style={{ margin: 0, color: 'var(--color-ink-2)', fontSize: 'var(--fs-md)' }}>
          This withdraws “{record.title}” — it disappears from the board and the client's portal.
          The requested items are kept as a record. This can't be undone.
        </p>
      </Modal>
    </>
  );
}

/**
 * ItemsTab — the requested_items for this request. Each item is Received (it has a
 * received_date) or Outstanding. Columns: item, category, status, received.
 */
function ItemsTab({
  items, loading, error, requestId, canEdit,
}: {
  items: RequestedItem[];
  loading: boolean;
  error: Error | null;
  requestId: string;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  // Staff manually toggle an item received (for docs that arrive OUTSIDE the portal —
  // emailed / dropped off). The parent request's status advances/reverts server-side;
  // invalidate the item list + the record + the board so the row and header pill refresh.
  const receiveMut = useMutation<unknown, Error, { itemId: string; received: boolean }>({
    mutationFn: ({ itemId, received }) => api.post(`/api/information-requests/${requestId}/items/${itemId}/receive`, { received }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['requested-items', requestId] });
      qc.invalidateQueries({ queryKey: ['information_requests', requestId] });
      qc.invalidateQueries({ queryKey: ['information-requests-board'] });
    },
    onError: (err) => toast({
      variant: 'error',
      message: err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error) : 'Could not update the item.',
    }),
  });

  const columns = useMemo<ColumnDef<RequestedItem>[]>(() => {
    const cols: ColumnDef<RequestedItem>[] = [
      {
        accessorKey: 'description',
        header: 'Item',
        cell: ({ row }) => (
          <span>
            <div style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>
              {row.original.description}
            </div>
            {row.original.notes && (
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', marginTop: 2 }}>
                {row.original.notes}
              </div>
            )}
          </span>
        ),
      },
      { accessorKey: 'category', header: 'Category', cell: ({ getValue }) => (getValue() as string | null) || '—' },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          row.original.received_date
            ? <Badge variant="success" dot>Received</Badge>
            : <Badge variant="warning" dot>Outstanding</Badge>
        ),
      },
      {
        accessorKey: 'received_date',
        header: 'Received date',
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? formatDate(v, { dateOnly: true }) : '—';
        },
      },
      {
        id: 'file',
        header: 'File',
        // When a portal upload fulfilled the item, let staff open the actual file
        // (same-origin download carries the session cookie). No file → em-dash.
        cell: ({ row }) => {
          const docId = row.original.document_id;
          if (!docId) return <span style={{ color: 'var(--color-ink-3)' }}>—</span>;
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => downloadDocument(docId, (m) => toast({ variant: 'error', message: m }))}
              title={row.original.document_name || 'Open the uploaded file'}
            >View file</Button>
          );
        },
      },
    ];
    if (canEdit) {
      cols.push({
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const done = !!row.original.received_date;
          return (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant={done ? 'ghost' : 'secondary'}
                size="sm"
                disabled={receiveMut.isPending}
                onClick={() => receiveMut.mutate({ itemId: row.original.id, received: !done })}
              >{done ? 'Mark outstanding' : 'Mark received'}</Button>
            </div>
          );
        },
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, receiveMut.isPending]);

  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading items…</p>;
  if (error) {
    return (
      <div style={{
        padding: 'var(--sp-4)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        Failed to load items. {error.message}
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div style={{
        padding: 'var(--sp-6)',
        background: 'var(--color-card)',
        border: '1px dashed var(--color-border)',
        borderRadius: 'var(--r-lg)',
        color: 'var(--color-muted)',
        textAlign: 'center',
        fontSize: 'var(--fs-md)',
      }}>No items have been added to this request yet.</div>
    );
  }
  return <DataTable data={items} columns={columns} />;
}

/**
 * PropertyListTab — the client's CURRENT property list (GET /api/properties?client_id=),
 * shown alongside the request so staff can confirm additions / deletions before the
 * client responds. This is the "attach current property list" step of the annual
 * information-request campaign; it reads the live properties list (not a snapshot),
 * so it always reflects the current portfolio.
 */
function PropertyListTab({
  properties, loading, error, hasClient, onOpenProperty,
}: {
  properties: ClientProperty[];
  loading: boolean;
  error: Error | null;
  hasClient: boolean;
  onOpenProperty: (id: string) => void;
}) {
  const columns = useMemo<ColumnDef<ClientProperty>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Property',
      cell: ({ row }) => (
        <span>
          <div style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>
            {row.original.name}
          </div>
          {row.original.location_code && (
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', marginTop: 2 }}>
              {row.original.location_code}
            </div>
          )}
        </span>
      ),
    },
    {
      accessorKey: 'city',
      header: 'Location',
      cell: ({ row }) => {
        const parts = [row.original.city, row.original.state].filter(Boolean);
        return parts.length ? parts.join(', ') : '—';
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const s = (getValue() as string | null) || 'active';
        return <Badge variant={s === 'active' ? 'success' : 'neutral'} dot>{s.replace(/_/g, ' ')}</Badge>;
      },
    },
  ], []);

  if (!hasClient) {
    return (
      <div style={{
        padding: 'var(--sp-6)',
        background: 'var(--color-card)',
        border: '1px dashed var(--color-border)',
        borderRadius: 'var(--r-lg)',
        color: 'var(--color-muted)',
        textAlign: 'center',
        fontSize: 'var(--fs-md)',
      }}>This request is not linked to a client, so there is no property list to show.</div>
    );
  }
  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading property list…</p>;
  if (error) {
    return (
      <div style={{
        padding: 'var(--sp-4)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        Failed to load the property list. {error.message}
      </div>
    );
  }
  if (properties.length === 0) {
    return (
      <div style={{
        padding: 'var(--sp-6)',
        background: 'var(--color-card)',
        border: '1px dashed var(--color-border)',
        borderRadius: 'var(--r-lg)',
        color: 'var(--color-muted)',
        textAlign: 'center',
        fontSize: 'var(--fs-md)',
      }}>This client has no properties on file.</div>
    );
  }
  return (
    <>
      <p style={{ margin: '0 0 var(--sp-3)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
        The client's current properties — confirm additions or deletions before they respond.
      </p>
      <DataTable data={properties} columns={columns} onRowClick={(row) => onOpenProperty(row.id)} />
    </>
  );
}
